import React from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { ChevronRight } from "@mui/icons-material";
import { Box, Typography, Card, CardContent, Grid, Divider } from "@mui/material";

import { WardType } from "../../constants/ward";
import { IpdSettingsPanel } from "../ResourceCentre/ModuleSettings/IpdSettings/IpdSettingsPanel";

// reusable card styles
const cardBase = {
  borderRadius: 3,
  backdropFilter: "blur(10px)",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
    transform: "translateY(-2px)"
  }
};

// Ward card component
function WardCard({
  title,
  description,
  action,
  onClick,
  disabled
}: {
  title: string;
  description: string;
  action: string;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactNode {
  return (
    <Card
      onClick={disabled ? undefined : onClick}
      sx={{
        ...cardBase,
        height: "150px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        "&:hover": disabled
          ? {
              boxShadow: cardBase.boxShadow,
              transform: "none"
            }
          : cardBase["&:hover"]
      }}
    >
      <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "1.25rem" }}>{title}</Typography>

        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: disabled ? "text.secondary" : "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: "auto"
          }}
        >
          {action} <ChevronRight />
        </Typography>
      </CardContent>
    </Card>
  );
}

const wards = [
  {
    type: WardType.GENERAL,
    title: "General Ward",
    description: "Manage general ward beds, room allocation, and patient admissions.",
    action: "Go to General Ward dashboard",
    path: `/ipd/wards/${WardType.GENERAL}`,
    comingSoon: false
  },
  {
    type: WardType.MEDICAL,
    title: "Medical Ward",
    description: "Handle medical ward admissions, critical bed management, and rapid patient flow.",
    action: "Go to Medical Ward dashboard",
    path: `/ipd/wards/${WardType.MEDICAL}`,
    comingSoon: false
  },
  {
    type: WardType.SURGICAL,
    title: "Surgical Ward",
    description: "Handle surgical admissions, critical bed management, and rapid patient flow.",
    action: "Go to Surgical Ward dashboard",
    path: `/ipd/wards/${WardType.SURGICAL}`,
    comingSoon: false
  },
  {
    type: WardType.MATERNITY,
    title: "Maternity Ward",
    description: "Manage maternity beds, mother-baby admissions, and postnatal care.",
    action: "Go to Maternity Ward dashboard",
    path: `/ipd/wards/${WardType.MATERNITY}`,
    comingSoon: false
  },
  {
    type: WardType.EMERGENCY,
    title: "Emergency Ward",
    description: "Handle emergency admissions, critical bed management, and rapid patient flow.",
    action: "Go to Emergency Ward dashboard",
    path: `/ipd/wards/${WardType.EMERGENCY}`,
    comingSoon: true
  },
  {
    type: WardType.INTENSIVE_CARE_UNIT,
    title: "Intensive Care Unit (ICU)",
    description: "Monitor ICU beds, critical patients, and intensive care resources.",
    action: "Go to ICU dashboard",
    path: `/ipd/wards/${WardType.INTENSIVE_CARE_UNIT}`,
    comingSoon: true
  }
];

// eslint-disable-next-line import/prefer-default-export
export function IpdDashboard(): React.ReactNode {
  const dispatch = useDispatch();

  return (
    <Box
      sx={{
        minHeight: "100%"
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            IPD Patient Management Center
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage wards, rooms, beds, admissions, and discharges
          </Typography>
        </Box>

        <IpdSettingsPanel />
      </Box>

      <Divider />

      {/* Wards Grid */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {wards.map((ward) => (
            <Grid key={ward.type} size={{ xs: 12, md: 6, lg: 3 }}>
              <WardCard
                title={ward.title}
                description={ward.description}
                action={ward.action}
                onClick={() => dispatch(push(ward.path))}
                disabled={ward.comingSoon}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
